import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DollarSign, FileText, Package, CreditCard } from 'lucide-react';
import "../../Styling/PurchaseDashboard.css";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import axios from 'axios';
import { Base_URL } from '../../url';
import { useAuth } from "../../Context/AuthContext";

const PurchaseDashboard = () => {
  const { user } = useAuth();
  
  // State for filters
  const [filters, setFilters] = useState({
    orgId: String(user?.organisationId || ""),
    companyCode: '',
    officeCode: '',
    storeCode: '',  
    deptCode: ''
  });

  // State for filter display names
  const [filterDisplay, setFilterDisplay] = useState({
    orgName: '',
    companyName: '',
    officeName: '',
    storeName: '',
    deptName: ''
  });

  // State for dropdown options
  const [orgList, setOrgList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [officeList, setOfficeList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [deptList, setDeptList] = useState([]);
  const dashboardRef = useRef(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [allData, setAllData] = useState([]);

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    totalPurchaseAmount: 0,
    goodReceiptNumber: 0,
    totalInvoice: 0,
    totalPayment: 0,
    purchaseByVendor: [],
    purchaseByItemCode: [],
    purchaseBySku: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Color palette for charts
  const CHART_COLORS = {
    vendor: ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe'],
    itemCode: ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0'],
    sku: ['#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe']
  };

  // ✅ Convert value to lakhs
  const toLakhs = (value) => {
    return value / 100000;
  };

  // ✅ Format value for display in lakhs
  const formatLakhs = (value) => {
    return toLakhs(value).toFixed(2);
  };

  // ✅ Format currency for display
  const formatCurrency = (value) => {
    return `₹${formatLakhs(value)} Lakh`;
  };

  // ✅ Fetch stores from store_Master table based on office code
  const fetchStoresByOffice = async (officeCode) => {
    try {
      const response = await axios.get(`${Base_URL}/PurchaseDashboard/stores-by-office?officeCode=${officeCode}`);
      setStoreList(response.data);
    } catch (err) {
      console.error("Error fetching stores:", err);
      setStoreList([]);
    }
  };

  // ✅ Fetch departments based on selected filters
  const fetchDepartments = async (orgId, companyCode) => {
    try {
      // Replace with your actual API endpoint
      const response = await axios.get(`${Base_URL}/PurchaseDashboard/departments?orgId=${orgId}&companyCode=${companyCode}`);
      setDeptList(response.data);
    } catch (err) {
      console.error("Error fetching departments:", err);
      setDeptList([]);
    }
  };

  // ✅ Fetch all purchase data from your API
  const fetchPurchaseData = async () => {
    setLoading(true);
    try {
      // TODO: Replace with your actual API endpoint
      const res = await axios.get(`${Base_URL}/PurchaseDashboard/GetPurchaseDashboardData`);
      setAllData(res.data);

      // Extract unique organizations with both code and name
      const orgs = [
        ...new Map(
          res.data.map((item) => [
            item.organisation_Id,
            { 
              orgId: item.organisation_Id, 
              orgName: item.org_Description 
            },
          ])
        ).values(),
      ];
      setOrgList(orgs);

      // ✅ Auto-select user's organization if available
      if (user?.organisationId && orgs.length > 0) {
        const userOrg = orgs.find(org => org.orgId == user.organisationId);
        if (userOrg) {
          setFilterDisplay(prev => ({
            ...prev,
            orgName: userOrg.orgName
          }));
          
          // Auto-populate companies for user's organization
          const userCompanies = [
            ...new Map(
              res.data
                .filter((x) => x.organisation_Id == user.organisationId)
                .map((x) => [
                  x.comapny_Code,
                  { 
                    companyCode: x.comapny_Code, 
                    companyName: x.company_Code_Description 
                  },
                ])
            ).values(),
          ];
          setCompanyList(userCompanies);
        }
      }

    } catch (err) {
      console.error("Error fetching purchase data:", err);
      setError("Failed to fetch purchase data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchaseData();
  }, []);

  // ✅ Apply filters automatically when data loads or filters change
  useEffect(() => {
    if (allData.length > 0) {
      handleApplyFilters();
    }
  }, [allData, filters.orgId, filters.companyCode, filters.officeCode, filters.storeCode, filters.deptCode]);

  // ✅ Handle Organization Change
  const handleOrgChange = (e) => {
    const selectedOrgId = e.target.value;
    const selectedOrg = orgList.find(org => org.orgId == selectedOrgId);

    setFilters({
      orgId: selectedOrgId,
      companyCode: "",
      officeCode: "",
      storeCode: "",
      deptCode: ""
    });

    setFilterDisplay({
      orgName: selectedOrg ? selectedOrg.orgName : "",
      companyName: "",
      officeName: "",
      storeName: "",
      deptName: ""
    });

    if (selectedOrgId) {
      // Filter companies based on selected organization
      const companies = [
        ...new Map(
          allData
            .filter((x) => x.organisation_Id == selectedOrgId)
            .map((x) => [
              x.comapny_Code,
              { 
                companyCode: x.comapny_Code, 
                companyName: x.company_Code_Description 
              },
            ])
        ).values(),
      ];
      setCompanyList(companies);
    } else {
      setCompanyList([]);
    }

    setOfficeList([]);
    setStoreList([]);
    setDeptList([]);
  };

  // ✅ Handle Company Change
  const handleCompanyChange = (e) => {
    const companyCode = e.target.value;
    const selectedCompany = companyList.find(company => company.companyCode == companyCode);

    setFilters({ 
      ...filters, 
      companyCode, 
      officeCode: "", 
      storeCode: "",
      deptCode: "" 
    });

    setFilterDisplay({
      ...filterDisplay,
      companyName: selectedCompany ? selectedCompany.companyName : "",
      officeName: "",
      storeName: "",
      deptName: ""
    });

    if (companyCode && filters.orgId) {
      const offices = [
        ...new Map(
          allData
            .filter(
              (x) =>
                x.organisation_Id == filters.orgId &&
                x.comapny_Code == companyCode
            )
            .map((x) => [
              x.officeCode,
              { 
                officeCode: x.officeCode, 
                officeName: x.office_code_Description 
              },
            ])
        ).values(),
      ];
      setOfficeList(offices);

      // Fetch departments for the selected organization and company
      fetchDepartments(filters.orgId, companyCode);
    } else {
      setOfficeList([]);
      setDeptList([]);
    }

    setStoreList([]);
  };

  // ✅ Handle Office Change
  const handleOfficeChange = (e) => {
    const officeCode = e.target.value;
    const selectedOffice = officeList.find(office => office.officeCode == officeCode);

    setFilters({ 
      ...filters, 
      officeCode, 
      storeCode: "" 
    });

    setFilterDisplay({
      ...filterDisplay,
      officeName: selectedOffice ? selectedOffice.officeName : "",
      storeName: ""
    });

    if (officeCode) {
      // Fetch stores from store_Master table based on office code
      fetchStoresByOffice(officeCode);
    } else {
      setStoreList([]);
    }
  };

  // ✅ Handle Store Change
  const handleStoreChange = (e) => {
    const storeCode = e.target.value;
    const selectedStore = storeList.find(store => store.storeCode == storeCode);

    setFilters({ ...filters, storeCode: storeCode });
    setFilterDisplay({
      ...filterDisplay,
      storeName: selectedStore ? selectedStore.storeName : ""
    });
  };

  // ✅ Handle Department Change
  const handleDeptChange = (e) => {
    const deptCode = e.target.value;
    const selectedDept = deptList.find(dept => dept.deptCode == deptCode);

    setFilters({ ...filters, deptCode: deptCode });
    setFilterDisplay({
      ...filterDisplay,
      deptName: selectedDept ? selectedDept.deptName : ""
    });
  };

  // ✅ Apply Filters and Calculate Dashboard Data
  const handleApplyFilters = () => {
    setLoading(true);
    try {
      const filteredData = allData.filter((item) => {
        return (
          (!filters.orgId || item.organisation_Id == filters.orgId) &&
          (!filters.companyCode || item.comapny_Code == filters.companyCode) &&
          (!filters.officeCode || item.officeCode == filters.officeCode) &&
          (!filters.storeCode || item.store_code == filters.storeCode) &&
          (!filters.deptCode || item.dept_code == filters.deptCode)
        );
      });

      // Calculate total values
      const totalPurchaseAmount = filteredData.reduce((sum, item) => sum + (item.total_Purchase_Amount || 0), 0);
      const goodReceiptNumber = filteredData.reduce((sum, item) => sum + (item.good_Receipt_Count || 0), 0);
      const totalInvoice = filteredData.reduce((sum, item) => sum + (item.invoice_Count || 0), 0);
      const totalPayment = filteredData.reduce((sum, item) => sum + (item.total_Payment || 0), 0);

      // Calculate chart data (convert to lakhs for display)
      const purchaseByVendor = calculateChartData(filteredData, 'vendor_Code', 'vendor_Name', 'purchase_Amount');
      const purchaseByItemCode = calculateChartData(filteredData, 'item_Code', 'item_Description', 'purchase_Amount');
      const purchaseBySku = calculateChartData(filteredData, 'sku_Code', 'sku_Description', 'purchase_Amount');

      setDashboardData({
        totalPurchaseAmount,
        goodReceiptNumber,
        totalInvoice,
        totalPayment,
        purchaseByVendor,
        purchaseByItemCode,
        purchaseBySku
      });

    } catch (err) {
      console.error("Error applying filters:", err);
      setError("Failed to apply filters");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Helper function to calculate chart data (values in lakhs)
  const calculateChartData = (data, codeField, descriptionField, valueField) => {
    const groupedData = data.reduce((acc, item) => {
      const code = item[codeField];
      const description = item[descriptionField];
      const value = toLakhs(item[valueField] || 0);
      
      if (code && description) {
        const key = `${code}-${description}`;
        if (!acc[key]) {
          acc[key] = { 
            name: description,
            code: code,
            value: 0 
          };
        }
        acc[key].value += value;
      }
      
      return acc;
    }, {});

    return Object.values(groupedData)
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  };

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      const element = dashboardRef.current;
      const canvas = await html2canvas(element, { 
        scale: 2,
        useCORS: true, 
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.8);
      
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgWidthPdf = imgWidth * ratio;
      const imgHeightPdf = imgHeight * ratio;
      
      pdf.addImage(imgData, "JPEG", 0, 0, imgWidthPdf, imgHeightPdf);
      pdf.save(`PurchaseDashboard_${filters.orgId || 'All'}_${new Date().toISOString().slice(0, 10)}.pdf`);
      
    } catch (err) {
      console.error("PDF Generation Error:", err);
      setError("Failed to generate PDF. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  };

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}</p>
          <p className="tooltip-value">
            ₹{payload[0].value.toFixed(2)} Lakh
          </p>
        </div>
      );
    }
    return null;
  };

  // Bar Chart Component
  const BarChartComponent = ({ data, title, color, dataKey }) => (
    <div className="chart-container">
      <h2 className="chart-title">
        <span className="chart-indicator"></span>
        {title}
      </h2>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} layout="vertical" margin={{ top: 20, right: 30, left: 100, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            type="number" 
            tickFormatter={(value) => `${value.toFixed(1)}L`} 
            tick={{fontSize: 11}}
          />
          <YAxis 
            dataKey="name" 
            type="category" 
            width={90} 
            tick={{fontSize: 11}}
            tickFormatter={(value) => value.length > 15 ? `${value.substring(0, 15)}...` : value}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar 
            dataKey="value" 
            fill={color}
            radius={[0, 4, 4, 0]}
            className="chart-bar"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  return (
    <div className="purchase-dashboard" ref={dashboardRef}>
      <div className="purchase-dashboard-container">
        {/* PDF Loading Indicator */}
        {pdfLoading && (
          <div className="purchase-pdf-loading">
            <div className="purchase-spinner"></div>
            <span>Generating PDF...</span>
          </div>
        )}
       
        <div className="purchase-dashboard-title">Purchase Dashboard</div>
        
        {/* Filters Section - All in one line */}
        <div className="purchase-filter-section">
          {/* Organization ID */}
          <div className="purchase-filter-card">
            <label className="purchase-filter-label">Organization ID</label>
            <select
              value={filters.orgId}
              onChange={handleOrgChange}
              className="purchase-filter-select"
            >
              <option value="">-- Select Organization --</option>
              {orgList.map((org) => (
                <option key={org.orgId} value={org.orgId}>
                  {org.orgId} - {org.orgName}
                </option>
              ))}
            </select>
          </div>

          {/* Company Code */}
          <div className="purchase-filter-card">
            <label className="purchase-filter-label">Company Code</label>
            <select
              value={filters.companyCode}
              onChange={handleCompanyChange}
              disabled={!filters.orgId}
              className="purchase-filter-select"
            >
              <option value="">-- Select Company --</option>
              {companyList.map((company) => (
                <option key={company.companyCode} value={company.companyCode}>
                  {company.companyCode} - {company.companyName}
                </option>
              ))}
            </select>
          </div>

          {/* Office Code */}
          <div className="purchase-filter-card">
            <label className="purchase-filter-label">Office Code</label>
            <select
              value={filters.officeCode}
              onChange={handleOfficeChange}
              disabled={!filters.orgId}
              className="purchase-filter-select"
            >
              <option value="">-- Select Office --</option>
              {officeList.map((office) => (
                <option key={office.officeCode} value={office.officeCode}>
                  {office.officeCode} - {office.officeName}
                </option>
              ))}
            </select>
          </div>

          {/* Store Code */}
          <div className="purchase-filter-card">
            <label className="purchase-filter-label">Store Code</label>
            <select
              value={filters.storeCode}
              onChange={handleStoreChange}
              disabled={!filters.officeCode}
              className="purchase-filter-select"
            >
              <option value="">-- Select Store --</option>
              {storeList.map((store) => (
                <option key={store.storeCode} value={store.storeCode}>
                  {store.storeCode} - {store.storeName}
                </option>
              ))}
            </select>
          </div>

          {/* Department Code */}
          <div className="purchase-filter-card">
            <label className="purchase-filter-label">Department Code</label>
            <select
              value={filters.deptCode}
              onChange={handleDeptChange}
              disabled={!filters.companyCode}
              className="purchase-filter-select"
            >
              <option value="">-- Select Department --</option>
              {deptList.map((dept) => (
                <option key={dept.deptCode} value={dept.deptCode}>
                  {dept.deptCode} - {dept.deptName}
                </option>
              ))}
            </select>
          </div>

          {/* Export Button */}
          <div className="purchase-filter-card">
            <label className="purchase-filter-label">&nbsp;</label>
            <button
              onClick={handleDownloadPDF}
              disabled={pdfLoading || loading}
              className="purchase-btn-export"
            >
              {pdfLoading ? (
                <>
                  <div className="purchase-spinner"></div>
                  Generating PDF...
                </>
              ) : (
                'Export to PDF'
              )}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="purchase-error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Statistics Cards Grid */}
        <div className="purchase-stats-grid">
          <div className="purchase-stat-card purchase-stat-total">
            <div className="purchase-stat-content">
              <div className="purchase-stat-text">
                <div className="purchase-stat-title">Total Purchase Amount</div>
                <div className="purchase-stat-value">{formatCurrency(dashboardData.totalPurchaseAmount)}</div>
              </div>
              <div className="purchase-stat-icon purchase-icon-total">
                <DollarSign className="purchase-icon" />
              </div>
            </div>
          </div>
          
          <div className="purchase-stat-card purchase-stat-receipt">
            <div className="purchase-stat-content">
              <div className="purchase-stat-text">
                <div className="purchase-stat-title">Good Receipt Number</div>
                <div className="purchase-stat-value">{dashboardData.goodReceiptNumber.toLocaleString()}</div>
              </div>
              <div className="purchase-stat-icon purchase-icon-receipt">
                <Package className="purchase-icon" />
              </div>
            </div>
          </div>
          
          <div className="purchase-stat-card purchase-stat-invoice">
            <div className="purchase-stat-content">
              <div className="purchase-stat-text">
                <div className="purchase-stat-title">Total Invoice</div>
                <div className="purchase-stat-value">{dashboardData.totalInvoice.toLocaleString()}</div>
              </div>
              <div className="purchase-stat-icon purchase-icon-invoice">
                <FileText className="purchase-icon" />
              </div>
            </div>
          </div>
          
          <div className="purchase-stat-card purchase-stat-payment">
            <div className="purchase-stat-content">
              <div className="purchase-stat-text">
                <div className="purchase-stat-title">Total Payment</div>
                <div className="purchase-stat-value">{formatCurrency(dashboardData.totalPayment)}</div>
              </div>
              <div className="purchase-stat-icon purchase-icon-payment">
                <CreditCard className="purchase-icon" />
              </div>
            </div>
          </div>
        </div>

        {/* Bar Charts Grid - All charts in one line */}
        <div className="purchase-charts-grid">
          <div className="purchase-chart-container">
            <div className="purchase-chart-header">
              <div className="purchase-chart-title">
                <span>Purchase by Vendor</span>
              </div>
            </div>
            <div className="purchase-chart-body">
              <BarChartComponent
                data={dashboardData.purchaseByVendor}
                title="Purchase by Vendor"
                color="#3b82f6"
                dataKey="value"
              />
            </div>
          </div>
          
          <div className="purchase-chart-container">
            <div className="purchase-chart-header">
              <div className="purchase-chart-title">
                <span>Purchase by Item Code</span>
              </div>
            </div>
            <div className="purchase-chart-body">
              <BarChartComponent
                data={dashboardData.purchaseByItemCode}
                title="Purchase by Item Code"
                color="#10b981"
                dataKey="value"
              />
            </div>
          </div>
          
          <div className="purchase-chart-container">
            <div className="purchase-chart-header">
              <div className="purchase-chart-title">
                <span>Purchase by SKU</span>
              </div>
            </div>
            <div className="purchase-chart-body">
              <BarChartComponent
                data={dashboardData.purchaseBySku}
                title="Purchase by SKU"
                color="#8b5cf6"
                dataKey="value"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseDashboard;